import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { supabase } from "../config";
import CompactYouTubePlayer, { formatSeconds } from "../components/CompactYouTubePlayer";
import {
  FaPlay, FaArrowLeft,
  FaChevronDown, FaChevronRight, FaPlus, FaSave, FaTimes, FaTrash,
  FaClock, FaYoutube
} from "react-icons/fa";

const ADMIN_PASSWORD = "kk26";

// ─── Helpers ──────────────────────────────────────────────

function timeToSec(t) {
  if (!t || typeof t !== "string") return null;
  const parts = t.split(":").map(Number);
  if (parts.some(isNaN)) return null;
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  if (parts.length === 1) return parts[0];
  return null;
}

function secToHMS(totalSec) {
  if (totalSec == null || isNaN(totalSec)) return "";
  totalSec = Math.floor(Number(totalSec));
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

// ─── TimestampField — input + seconds display ────────────

const TimestampField = ({ value, onChange, placeholder = "H:MM:SS", color = "blue" }) => {
  const sec = timeToSec(value);
  const colorMap = {
    blue:   { bg: "bg-blue-500/10",   border: "border-blue-500/30" },
    green:  { bg: "bg-green-500/10",  border: "border-green-500/30" },
    amber:  { bg: "bg-amber-500/10",  border: "border-amber-500/30" },
    purple: { bg: "bg-purple-500/10", border: "border-purple-500/30" },
    cyan:   { bg: "bg-cyan-500/10",   border: "border-cyan-500/30" },
    rose:   { bg: "bg-rose-500/10",   border: "border-rose-500/30" },
  };
  const c = colorMap[color] || colorMap.blue;

  return (
    <div className="flex items-center gap-1.5">
      <input
        type="text"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={`w-24 px-2 py-1.5 ${c.bg} border ${c.border} rounded text-white text-sm font-mono text-center
          placeholder:text-white/20 focus:outline-none focus:ring-2 focus:ring-white/20`}
      />
      {sec != null && (
        <span className="text-[10px] text-white/25 font-mono">{sec}s</span>
      )}
    </div>
  );
};

// ─── TimestampSection — a labeled section with Start/End + "Set" buttons ──

const TimestampSection = ({
  sectionLabel, sectionIcon, sectionColor,
  startVal, endVal,
  onStartChange, onEndChange,
  onSetStart, onSetEnd,
  startColor, endColor,
  onPlayStart,
}) => {
  const duration = (startVal && endVal && timeToSec(startVal) != null && timeToSec(endVal) != null)
    ? timeToSec(endVal) - timeToSec(startVal)
    : null;

  const btnBase = "px-3 py-2 text-white text-xs rounded-lg font-semibold transition-all active:scale-95 flex items-center gap-1.5 shadow-sm";
  const startBtnColor = {
    blue: "bg-blue-600 hover:bg-blue-500 shadow-blue-900/30",
    cyan: "bg-cyan-600 hover:bg-cyan-500 shadow-cyan-900/30",
    amber: "bg-amber-600 hover:bg-amber-500 shadow-amber-900/30",
  };
  const endBtnColor = {
    green: "bg-green-600 hover:bg-green-500 shadow-green-900/30",
    rose: "bg-rose-600 hover:bg-rose-500 shadow-rose-900/30",
    purple: "bg-purple-600 hover:bg-purple-500 shadow-purple-900/30",
  };

  return (
    <div className={`rounded-lg border border-white/8 bg-white/[0.02] p-3`}>
      {/* Section label */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className={`text-sm font-bold ${sectionColor || "text-kk-gold"}`}>
            {sectionIcon} {sectionLabel}
          </span>
          {duration != null && duration > 0 && (
            <span className="text-[10px] text-white/30 font-mono bg-white/5 px-2 py-0.5 rounded">
              {Math.floor(duration / 60)}m {duration % 60}s
            </span>
          )}
        </div>
        {startVal && timeToSec(startVal) != null && onPlayStart && (
          <button
            onClick={() => onPlayStart(timeToSec(startVal))}
            className="text-[10px] text-kk-gold/50 hover:text-kk-gold flex items-center gap-1 transition-colors"
          >
            <FaPlay className="text-[7px]" /> Play
          </button>
        )}
      </div>

      {/* Start row */}
      <div className="flex items-center gap-3 mb-2">
        <TimestampField value={startVal} onChange={onStartChange} color={startColor} />
        <button onClick={onSetStart} className={`${btnBase} ${startBtnColor[startColor] || startBtnColor.blue}`}>
          <FaClock className="text-[10px]" /> Set Start Time
        </button>
      </div>

      {/* End row */}
      <div className="flex items-center gap-3">
        <TimestampField value={endVal} onChange={onEndChange} color={endColor} />
        <button onClick={onSetEnd} className={`${btnBase} ${endBtnColor[endColor] || endBtnColor.green}`}>
          <FaClock className="text-[10px]" /> Set End Time
        </button>
      </div>
    </div>
  );
};

// ─── getValue helper — resolves edit state or falls back to DB value ──

function getFieldValue(perf, edits, field) {
  const e = edits[perf.performance_id] || {};
  if (e[field] !== undefined) return e[field];
  // Convert _sec to HMS for display if _time is empty
  if (field.endsWith("_time")) {
    const secField = field.replace("_time", "_sec");
    const secVal = e[secField] !== undefined ? e[secField] : perf[secField];
    if (secVal != null && secVal !== "" && !isNaN(secVal)) return secToHMS(secVal);
    return perf[field] || "";
  }
  return perf[field] || "";
}

// ─── IntroSection — Introduction timestamps for a storyteller ──

const IntroSection = ({ perf, edits, setFieldValue, currentTime, onDelete, isNew, playFrom }) => {
  const pid = perf.performance_id;
  const gv = (field) => getFieldValue(perf, edits, field);

  return (
    <div className={`rounded-xl border-2 ${isNew ? "border-cyan-500/40 bg-cyan-500/5" : "border-cyan-500/20 bg-cyan-900/10"} p-4`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold text-cyan-300 bg-cyan-500/15 px-2.5 py-0.5 rounded-full">
            పరిచయం Introduction
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-white/20 font-mono">PID:{pid}</span>
          {onDelete && (
            <button onClick={() => onDelete(pid)} className="text-red-400/50 hover:text-red-400 transition-colors" title="Remove">
              <FaTrash className="text-xs" />
            </button>
          )}
        </div>
      </div>

      <TimestampSection
        sectionLabel="పరిచయం / Introduction"
        sectionIcon="🎤"
        sectionColor="text-cyan-300"
        startVal={gv("story_start_time")}
        endVal={gv("story_end_time")}
        onStartChange={(v) => setFieldValue(pid, "story_start_time", v)}
        onEndChange={(v) => setFieldValue(pid, "story_end_time", v)}
        onSetStart={() => setFieldValue(pid, "story_start_time", secToHMS(currentTime))}
        onSetEnd={() => setFieldValue(pid, "story_end_time", secToHMS(currentTime))}
        startColor="cyan"
        endColor="rose"
        onPlayStart={playFrom}
      />
    </div>
  );
};

// ─── StorySection — one performance row with story + discussion timestamps ──

const StorySection = ({ perf, index, edits, setFieldValue, currentTime, onDelete, isNew, playFrom }) => {
  const pid = perf.performance_id;
  const gv = (field) => getFieldValue(perf, edits, field);

  const storyTitle = perf.story_title || (perf.stories && perf.stories.title_telugu) || "";
  const writerName = perf.writer_name || (perf.stories && perf.stories.writers && perf.stories.writers.name_telugu) || "";

  return (
    <div className={`rounded-xl border-2 ${isNew ? "border-green-500/30 bg-green-500/5" : "border-kk-gold/15 bg-white/[0.02]"} p-4`}>
      {/* Story header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-bold text-kk-gold bg-kk-gold/10 px-2.5 py-0.5 rounded-full">
            Story {index + 1}
          </span>
          {storyTitle && (
            <span className="text-sm text-kk-cream font-krushna">"{storyTitle}"</span>
          )}
          {writerName && (
            <span className="text-xs text-white/40">— {writerName}</span>
          )}
          {perf.is_author && (
            <span className="text-[9px] px-1.5 py-0.5 bg-purple-500/20 text-purple-300 rounded">రచయిత</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-white/20 font-mono">PID:{pid}</span>
          {onDelete && (
            <button onClick={() => onDelete(pid)} className="text-red-400/50 hover:text-red-400 transition-colors" title="Remove story">
              <FaTrash className="text-xs" />
            </button>
          )}
        </div>
      </div>

      {/* Story timestamps */}
      <div className="space-y-3">
        <TimestampSection
          sectionLabel="కథ / Story"
          sectionIcon="📖"
          sectionColor="text-blue-300"
          startVal={gv("story_start_time")}
          endVal={gv("story_end_time")}
          onStartChange={(v) => setFieldValue(pid, "story_start_time", v)}
          onEndChange={(v) => setFieldValue(pid, "story_end_time", v)}
          onSetStart={() => setFieldValue(pid, "story_start_time", secToHMS(currentTime))}
          onSetEnd={() => setFieldValue(pid, "story_end_time", secToHMS(currentTime))}
          startColor="blue"
          endColor="green"
          onPlayStart={playFrom}
        />

        <TimestampSection
          sectionLabel="చర్చ / Discussion"
          sectionIcon="💬"
          sectionColor="text-amber-300"
          startVal={gv("discussion_start_time")}
          endVal={gv("discussion_end_time")}
          onStartChange={(v) => setFieldValue(pid, "discussion_start_time", v)}
          onEndChange={(v) => setFieldValue(pid, "discussion_end_time", v)}
          onSetStart={() => setFieldValue(pid, "discussion_start_time", secToHMS(currentTime))}
          onSetEnd={() => setFieldValue(pid, "discussion_end_time", secToHMS(currentTime))}
          startColor="amber"
          endColor="purple"
          onPlayStart={playFrom}
        />
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// ─── Main Component ──────────────────────────────────────
// ═══════════════════════════════════════════════════════════

const EpisodeTimestamps = () => {
  const { episodeId } = useParams();
  const navigate = useNavigate();

  // Auth
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");

  // Data
  const [episode, setEpisode] = useState(null);
  const [performances, setPerformances] = useState([]);
  const [stories, setStories] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  // Edits tracking: { [performance_id]: { field: value, ... } }
  const [edits, setEdits] = useState({});
  const [newPerfs, setNewPerfs] = useState(new Set()); // newly added performance IDs
  const [isSaving, setIsSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  // Player — shared CompactYouTubePlayer, controlled via ref
  const [currentTime, setCurrentTime] = useState(0);
  const playerRef = useRef(null);

  // UI
  const [collapsed, setCollapsed] = useState({}); // { storytellerKey: boolean }
  const [episodeList, setEpisodeList] = useState([]);

  // ─── Auth ───
  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
    } else {
      setErrorMsg("Incorrect password.");
    }
  };

  // ─── Data Fetching ───
  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      setNotFound(false);

      // Fetch episode
      const { data: ep, error: epErr } = await supabase
        .from("episodes")
        .select("*")
        .eq("episode_id", episodeId)
        .single();

      if (epErr || !ep) { setNotFound(true); return; }
      setEpisode(ep);

      // Fetch performances
      const { data: perfData } = await supabase
        .from("performances")
        .select("*")
        .eq("episode_id", episodeId)
        .order("story_start_sec", { ascending: true, nullsFirst: true });

      const perfs = perfData || [];
      setPerformances(perfs);

      // Fetch stories for these performances
      const storyIds = [...new Set(perfs.map(p => p.story_id).filter(Boolean))];
      if (storyIds.length > 0) {
        const { data: storyData } = await supabase
          .from("stories")
          .select("story_id, title_telugu, writer_id, writers(name_telugu)")
          .in("story_id", storyIds);
        if (storyData) {
          const map = {};
          storyData.forEach(s => map[s.story_id] = s);
          setStories(map);
        }
      }

      // Fetch episode list for navigation
      const { data: epList } = await supabase
        .from("episodes")
        .select("episode_id, title, video_id, publish_date")
        .not("video_id", "is", null)
        .order("publish_date", { ascending: false })
        .limit(200);
      if (epList) setEpisodeList(epList);

    } catch (err) {
      console.error("Error:", err);
      setNotFound(true);
    } finally {
      setIsLoading(false);
    }
  }, [episodeId]);

  useEffect(() => {
    if (isAuthenticated) fetchData();
  }, [isAuthenticated, fetchData]);

  // ─── Group performances by storyteller ───
  // Detect if a performance is an "introduction" record (no story, category = 'introduction')
  const isIntroPerf = useCallback((p) => {
    return p.category === "introduction" || (p.story_id == null && p.category === "introduction");
  }, []);

  const grouped = useMemo(() => {
    const map = new Map();
    performances.forEach(p => {
      const key = p.storyteller_name_telugu || p.storyteller_name_english || `unknown_${p.performance_id}`;
      if (!map.has(key)) {
        map.set(key, {
          key,
          telugu: p.storyteller_name_telugu || "",
          english: p.storyteller_name_english || "",
          titlePrefix: p.title || "",
          storyteller_id: p.storyteller_id,
          introPerf: null,       // introduction performance (if any)
          storyPerfs: [],        // story performances
        });
      }
      // Enrich with story data
      const enriched = { ...p };
      if (p.story_id && stories[p.story_id]) {
        enriched.stories = stories[p.story_id];
        if (!enriched.story_title) enriched.story_title = stories[p.story_id].title_telugu;
        if (!enriched.writer_name) {
          const w = stories[p.story_id].writers;
          if (w) enriched.writer_name = w.name_telugu;
        }
      }

      const group = map.get(key);
      if (isIntroPerf(p)) {
        group.introPerf = enriched;
      } else {
        group.storyPerfs.push(enriched);
      }
    });

    // Sort story performances within each group by start_sec
    map.forEach(group => {
      group.storyPerfs.sort((a, b) => (a.story_start_sec || 99999) - (b.story_start_sec || 99999));
    });

    return [...map.values()];
  }, [performances, stories, isIntroPerf]);

  // ─── Edit helpers ───
  const setFieldValue = useCallback((perfId, field, value) => {
    setEdits(prev => ({
      ...prev,
      [perfId]: { ...prev[perfId], [field]: value },
    }));
  }, []);

  const isDirty = useMemo(() => Object.keys(edits).length > 0, [edits]);

  // ─── Save ───
  const handleSave = async () => {
    const dirtyIds = Object.keys(edits);
    if (dirtyIds.length === 0) return;

    setIsSaving(true);
    setSaveMsg("");
    setErrorMsg("");

    let saved = 0;
    let errors = 0;

    for (const pidStr of dirtyIds) {
      const pid = Number(pidStr);
      const fields = edits[pid];
      const updateObj = {};

      // Process each time field — compute _sec from _time
      for (const [key, val] of Object.entries(fields)) {
        if (key.endsWith("_time")) {
          updateObj[key] = val || null;
          const secKey = key.replace("_time", "_sec");
          updateObj[secKey] = timeToSec(val);
        }
      }

      if (Object.keys(updateObj).length === 0) continue;

      const { error } = await supabase
        .from("performances")
        .update(updateObj)
        .eq("performance_id", pid);

      if (error) {
        console.error(`Failed to save PID ${pid}:`, error);
        errors++;
      } else {
        saved++;
      }
    }

    setIsSaving(false);
    if (errors > 0) {
      setErrorMsg(`Saved ${saved}, failed ${errors}.`);
    } else {
      setSaveMsg(`Saved ${saved} performance(s).`);
      setEdits({});
      setNewPerfs(new Set());
      // Refresh data
      await fetchData();
    }

    setTimeout(() => { setSaveMsg(""); setErrorMsg(""); }, 4000);
  };

  // ─── Add Story for a storyteller ───
  // ─── Add Introduction for a storyteller ───
  const handleAddIntro = async (group) => {
    setErrorMsg("");
    const { data, error } = await supabase
      .from("performances")
      .insert({
        episode_id: Number(episodeId),
        storyteller_name_telugu: group.telugu || null,
        storyteller_name_english: group.english || null,
        storyteller_id: group.storyteller_id || null,
        title: group.titlePrefix || null,
        category: "introduction",
      })
      .select()
      .single();

    if (error) {
      setErrorMsg(`Failed to add introduction: ${error.message}`);
      return;
    }

    setNewPerfs(prev => new Set([...prev, data.performance_id]));
    setPerformances(prev => [...prev, data]);
  };

  // ─── Add Story for a storyteller ───
  const handleAddStory = async (group) => {
    setErrorMsg("");
    const { data, error } = await supabase
      .from("performances")
      .insert({
        episode_id: Number(episodeId),
        storyteller_name_telugu: group.telugu || null,
        storyteller_name_english: group.english || null,
        storyteller_id: group.storyteller_id || null,
        title: group.titlePrefix || null,
      })
      .select()
      .single();

    if (error) {
      setErrorMsg(`Failed to add story: ${error.message}`);
      return;
    }

    setNewPerfs(prev => new Set([...prev, data.performance_id]));
    setPerformances(prev => [...prev, data]);
  };

  // ─── Delete a newly added (empty) performance ───
  const handleDeletePerf = async (pid) => {
    if (!newPerfs.has(pid)) return; // Only allow deleting newly added rows
    const { error } = await supabase.from("performances").delete().eq("performance_id", pid);
    if (error) {
      setErrorMsg(`Failed to delete: ${error.message}`);
      return;
    }
    setPerformances(prev => prev.filter(p => p.performance_id !== pid));
    setNewPerfs(prev => { const s = new Set(prev); s.delete(pid); return s; });
    setEdits(prev => { const copy = { ...prev }; delete copy[pid]; return copy; });
  };

  // ─── Toggle collapse ───
  const toggleCollapse = (key) => {
    setCollapsed(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // ─── Episode navigation ───
  const currentEpIdx = useMemo(
    () => episodeList.findIndex(e => String(e.episode_id) === String(episodeId)),
    [episodeList, episodeId]
  );
  const prevEp = currentEpIdx > 0 ? episodeList[currentEpIdx - 1] : null;
  const nextEp = currentEpIdx < episodeList.length - 1 ? episodeList[currentEpIdx + 1] : null;

  // ─── Play from timestamp ───
  const playFrom = useCallback((sec) => {
    playerRef.current?.playFrom(sec);
  }, []);

  // ═══ Auth screen ═══
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-kk-ink flex items-center justify-center">
        <div className="bg-white/5 border border-kk-gold/20 rounded-xl p-8 max-w-md w-full">
          <h1 className="font-krushna text-kk-gold text-2xl mb-6 text-center">
            Episode Timestamps Editor
          </h1>
          <form onSubmit={handleLogin}>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter admin password"
              className="w-full px-4 py-3 bg-white/5 border border-kk-gold/20 rounded-lg text-white mb-4
                focus:ring-2 focus:ring-kk-gold focus:border-transparent placeholder:text-white/30"
            />
            <button
              type="submit"
              className="w-full bg-kk-gold text-kk-ink py-3 rounded-lg hover:bg-kk-amber font-semibold transition-colors"
            >
              Login
            </button>
            {errorMsg && <p className="text-red-400 text-sm mt-2">{errorMsg}</p>}
          </form>
        </div>
      </div>
    );
  }

  // ═══ Loading ═══
  if (isLoading) {
    return (
      <div className="min-h-screen bg-kk-ink flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-kk-gold/30 border-t-kk-gold rounded-full animate-spin mx-auto mb-6" />
          <p className="font-krushna text-kk-gold text-2xl">Loading...</p>
        </div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-kk-ink flex flex-col items-center justify-center text-center px-6">
        <h1 className="text-2xl font-bold text-kk-cream mb-4">Episode Not Found</h1>
        <Link to="/episode-creator" className="px-6 py-2 bg-kk-gold text-kk-ink rounded-full font-semibold hover:bg-kk-amber">
          Back to Episodes
        </Link>
      </div>
    );
  }

  const videoId = episode?.video_id || episode?.youtube_broadcast_id;

  // ═══ Main Render ═══
  return (
    <div className="min-h-screen bg-kk-ink text-white">

      {/* ═══ STICKY PLAYER SECTION ═══ */}
      <div className="sticky top-0 z-30 bg-kk-ink border-b border-kk-gold/10">

        {/* Top bar */}
        <div className="bg-kk-ink/95 border-b border-kk-gold/5 px-4 py-2">
          <div className="max-w-6xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/episode-creator" className="flex items-center gap-2 text-kk-gold/70 hover:text-kk-gold text-sm transition-colors">
                <FaArrowLeft className="text-xs" /> Episodes
              </Link>
              <span className="text-white/20">|</span>
              <Link to={`/episodes/${episodeId}`} className="text-kk-gold/50 hover:text-kk-gold text-xs transition-colors">
                View Page
              </Link>
            </div>

            {/* Episode nav */}
            <div className="flex items-center gap-3">
              {nextEp && (
                <Link to={`/episode-timestamps/${nextEp.episode_id}`}
                  className="text-xs text-kk-gold/50 hover:text-kk-gold transition-colors">
                  ← Older
                </Link>
              )}
              <span className="text-white/20 text-xs font-mono">
                Ep {episode.episode_id}
              </span>
              {prevEp && (
                <Link to={`/episode-timestamps/${prevEp.episode_id}`}
                  className="text-xs text-kk-gold/50 hover:text-kk-gold transition-colors">
                  Newer →
                </Link>
              )}
            </div>
          </div>
        </div>

        {/* Player */}
        {videoId && (
          <CompactYouTubePlayer ref={playerRef} videoId={videoId} onTimeUpdate={setCurrentTime} sticky={false} />
        )}
      </div>

      {/* ═══ EPISODE HEADER ═══ */}
      <div className="max-w-4xl mx-auto px-4 pt-4 pb-2">
        <h1 className="font-krushna text-kk-cream text-lg leading-tight mb-1">
          {episode.title || `Episode ${episode.episode_id}`}
        </h1>
        <div className="flex items-center gap-3 text-xs text-white/40">
          {episode.publish_date && <span>{episode.publish_date}</span>}
          {videoId && (
            <a href={`https://youtube.com/watch?v=${videoId}`} target="_blank" rel="noreferrer"
              className="flex items-center gap-1 text-red-400/60 hover:text-red-400 transition-colors">
              <FaYoutube /> {videoId}
            </a>
          )}
          <span>{performances.length} performances</span>
        </div>
      </div>

      {/* ═══ SAVE BAR ═══ */}
      {isDirty && (
        <div className="sticky top-0 z-20 bg-kk-ink/95 border-y border-kk-gold/20 backdrop-blur-sm">
          <div className="max-w-4xl mx-auto px-4 py-2 flex items-center justify-between">
            <span className="text-xs text-amber-400">
              {Object.keys(edits).length} unsaved change(s)
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => { setEdits({}); }}
                className="px-3 py-1.5 text-xs text-white/50 hover:text-white border border-white/10 rounded transition-colors"
              >
                <FaTimes className="inline mr-1 text-[9px]" /> Discard
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="px-4 py-1.5 bg-green-600 hover:bg-green-700 text-white text-xs rounded font-medium
                  disabled:opacity-50 transition-colors flex items-center gap-1"
              >
                <FaSave className="text-[10px]" /> {isSaving ? "Saving..." : "Save All"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Status messages */}
      {saveMsg && (
        <div className="max-w-4xl mx-auto px-4 pt-2">
          <div className="bg-green-500/10 border border-green-500/30 rounded-lg px-4 py-2 text-green-300 text-sm">{saveMsg}</div>
        </div>
      )}
      {errorMsg && (
        <div className="max-w-4xl mx-auto px-4 pt-2">
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-2 text-red-300 text-sm">{errorMsg}</div>
        </div>
      )}

      {/* ═══ STORYTELLER SECTIONS ═══ */}
      <div className="max-w-4xl mx-auto px-4 py-4 space-y-4">
        {grouped.length === 0 ? (
          <div className="text-center py-12 text-white/30">
            <p className="text-lg mb-2">No performances found for this episode.</p>
            <p className="text-sm">Assign storytellers in the Episode Creator first.</p>
          </div>
        ) : (
          grouped.map((group) => {
            const isCollapsed = collapsed[group.key];
            const { introPerf, storyPerfs } = group;
            const allPerfs = [...(introPerf ? [introPerf] : []), ...storyPerfs];
            const hasAnyTimestamp = allPerfs.some(p =>
              p.story_start_sec || edits[p.performance_id]?.story_start_time
            );
            const totalSections = (introPerf ? 1 : 0) + storyPerfs.length;

            return (
              <div
                key={group.key}
                className="border border-kk-gold/15 rounded-xl overflow-hidden bg-white/[0.02]"
              >
                {/* Storyteller header */}
                <button
                  onClick={() => toggleCollapse(group.key)}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/[0.03] transition-colors text-left"
                >
                  <span className="text-kk-gold/60 text-xs">
                    {isCollapsed ? <FaChevronRight /> : <FaChevronDown />}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-krushna text-kk-cream text-base">
                        {group.titlePrefix ? `${group.titlePrefix} ` : ""}{group.telugu || group.english || "—"}
                      </span>
                      {group.english && group.telugu && (
                        <span className="text-xs text-white/30">({group.english})</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {introPerf && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-cyan-500/10 text-cyan-400/70">
                        intro
                      </span>
                    )}
                    <span className={`text-[10px] px-2 py-0.5 rounded ${
                      hasAnyTimestamp
                        ? "bg-green-500/10 text-green-400/80"
                        : "bg-red-500/10 text-red-400/60"
                    }`}>
                      {storyPerfs.length} {storyPerfs.length === 1 ? "story" : "stories"}
                    </span>
                    {hasAnyTimestamp && (
                      <span className="text-[10px] text-white/20">
                        {formatSeconds(Math.min(...allPerfs.map(p => p.story_start_sec || 99999).filter(v => v < 99999)))}
                      </span>
                    )}
                  </div>
                </button>

                {/* Expanded content */}
                {!isCollapsed && (
                  <div className="px-4 pb-4 space-y-3 border-t border-white/5">

                    {/* Quick seek row */}
                    {hasAnyTimestamp && (
                      <div className="flex items-center gap-2 pt-3 pb-1 flex-wrap">
                        <span className="text-[10px] text-white/30">Quick seek:</span>
                        {introPerf && introPerf.story_start_sec && (
                          <button
                            onClick={() => playFrom(introPerf.story_start_sec)}
                            className="px-2 py-0.5 bg-cyan-500/10 border border-cyan-500/20 rounded text-cyan-300 text-[10px]
                              hover:bg-cyan-500/20 transition-colors font-mono"
                          >
                            Intro @ {formatSeconds(introPerf.story_start_sec)}
                          </button>
                        )}
                        {storyPerfs.filter(p => p.story_start_sec).map((p, i) => (
                          <button
                            key={p.performance_id}
                            onClick={() => playFrom(p.story_start_sec)}
                            className="px-2 py-0.5 bg-blue-500/10 border border-blue-500/20 rounded text-blue-300 text-[10px]
                              hover:bg-blue-500/20 transition-colors font-mono"
                          >
                            Story {i + 1} @ {formatSeconds(p.story_start_sec)}
                          </button>
                        ))}
                      </div>
                    )}

                    {/* ── 1. Introduction section ── */}
                    {introPerf ? (
                      <IntroSection
                        perf={introPerf}
                        edits={edits}
                        setFieldValue={setFieldValue}
                        currentTime={currentTime}
                        onDelete={newPerfs.has(introPerf.performance_id) ? handleDeletePerf : null}
                        isNew={newPerfs.has(introPerf.performance_id)}
                        playFrom={playFrom}
                      />
                    ) : (
                      <button
                        onClick={() => handleAddIntro(group)}
                        className="flex items-center gap-2 px-3 py-2 border border-dashed border-cyan-500/25 rounded-lg
                          text-cyan-400/50 text-xs hover:text-cyan-300 hover:border-cyan-400/40 transition-colors w-full justify-center"
                      >
                        <FaPlus className="text-[9px]" /> Add Introduction
                      </button>
                    )}

                    {/* ── 2-N. Story sections ── */}
                    {storyPerfs.map((perf, idx) => (
                      <StorySection
                        key={perf.performance_id}
                        perf={perf}
                        index={idx}
                        edits={edits}
                        setFieldValue={setFieldValue}
                        currentTime={currentTime}
                        onDelete={newPerfs.has(perf.performance_id) ? handleDeletePerf : null}
                        isNew={newPerfs.has(perf.performance_id)}
                        playFrom={playFrom}
                      />
                    ))}

                    {/* Add story button */}
                    <button
                      onClick={() => handleAddStory(group)}
                      className="flex items-center gap-2 px-3 py-2 border border-dashed border-kk-gold/20 rounded-lg
                        text-kk-gold/50 text-xs hover:text-kk-gold hover:border-kk-gold/40 transition-colors w-full justify-center"
                    >
                      <FaPlus className="text-[9px]" /> Add Story
                    </button>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Bottom padding */}
      <div className="h-20" />
    </div>
  );
};

export default EpisodeTimestamps;
